"use client";

import Link from "next/link";
import { ChevronRight } from "lucide-react";
import { cn, ORDER_STATUS_LABELS } from "@/lib/utils";
import type { OrderStatus } from "@/types";

export const accountSurface =
  "border bg-[var(--sf-card-bg,var(--es-surface,#fff))] shadow-[var(--es-shadow-sm)]";

export function AccountSurface({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(accountSurface, "rounded-lg border-[var(--es-border-soft)]", className)}
    >
      {children}
    </section>
  );
}

export function AccountPageHeader({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow?: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        {eyebrow && (
          <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.16em] text-[var(--es-text-muted)]">
            {eyebrow}
          </p>
        )}
        <h1 className="text-2xl font-bold leading-tight text-[var(--sf-foreground)] sm:text-3xl">
          {title}
        </h1>
        {description && (
          <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--es-text-muted)]">
            {description}
          </p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </header>
  );
}

export function AccountSectionHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="text-sm font-bold uppercase tracking-[0.12em] text-[var(--sf-foreground)]">
          {title}
        </h2>
        {description && (
          <p className="mt-1 text-sm text-[var(--es-text-muted)]">{description}</p>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function AccountMetric({
  icon: Icon,
  label,
  value,
  helper,
  tone = "default",
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  helper?: string;
  tone?: "default" | "accent" | "success";
}) {
  const iconClass =
    tone === "accent"
      ? "bg-[var(--sf-accent)] text-black"
      : tone === "success"
        ? "bg-[var(--sf-success-bg)] text-[var(--sf-success-fg)]"
        : "bg-[color-mix(in_srgb,var(--sf-foreground)_7%,transparent)] text-[var(--sf-foreground)]";

  return (
    <AccountSurface className="p-4 transition duration-200 hover:-translate-y-0.5 hover:border-[var(--es-border)]">
      <div className="flex min-h-20 items-start gap-3">
        <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-md", iconClass)}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--es-text-muted)]">
            {label}
          </p>
          <p className="mt-1 text-xl font-bold leading-tight text-[var(--sf-foreground)]">
            {value}
          </p>
          {helper && <p className="mt-1 text-xs text-[var(--es-text-muted)]">{helper}</p>}
        </div>
      </div>
    </AccountSurface>
  );
}

export function AccountEmptyState({
  icon: Icon,
  title,
  description,
  href,
  actionLabel,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  href: string;
  actionLabel: string;
}) {
  return (
    <AccountSurface className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[color-mix(in_srgb,var(--sf-foreground)_6%,transparent)] text-[var(--sf-foreground)]">
        <Icon className="h-5 w-5 opacity-70" />
      </div>
      <h3 className="mt-4 text-base font-bold text-[var(--sf-foreground)]">{title}</h3>
      <p className="mt-1 max-w-lg text-sm leading-6 text-[var(--es-text-muted)]">
        {description}
      </p>
      <Link
        href={href}
        className="account-primary-action mt-5 inline-flex min-h-11 items-center gap-2 rounded-md px-4 text-sm font-bold transition duration-200"
      >
        {actionLabel}
        <ChevronRight className="h-4 w-4" />
      </Link>
    </AccountSurface>
  );
}

const ORDER_STEPS: Array<{ key: OrderStatus; label: string }> = [
  { key: "Pending", label: "Alındı" },
  { key: "Processing", label: "Hazırlanıyor" },
  { key: "Shipped", label: "Kargoda" },
  { key: "Delivered", label: "Teslim" },
];

export function OrderStatusTimeline({ status }: { status: OrderStatus }) {
  if (status === "Cancelled" || status === "Refunded" || status === "OnHold") {
    return (
      <div className="rounded-md border border-[var(--es-border-soft)] bg-[color-mix(in_srgb,var(--sf-foreground)_4%,transparent)] px-3 py-2 text-sm text-[var(--es-text-muted)]">
        Bu siparişin durumu: <span className="font-semibold text-[var(--sf-foreground)]">{ORDER_STATUS_LABELS[status]}</span>
      </div>
    );
  }

  const currentIndex = Math.max(ORDER_STEPS.findIndex((step) => step.key === status), 0);

  return (
    <div className="grid grid-cols-4 gap-2">
      {ORDER_STEPS.map((step, index) => {
        const complete = index <= currentIndex;
        return (
          <div key={step.key} className="min-w-0">
            <div
              className={cn(
                "h-1.5 rounded-full transition-colors",
                complete ? "bg-[var(--sf-accent)]" : "bg-[color-mix(in_srgb,var(--sf-foreground)_10%,transparent)]"
              )}
            />
            <p
              className={cn(
                "mt-2 truncate text-xs font-semibold",
                complete ? "text-[var(--sf-foreground)]" : "text-[var(--es-text-muted)]"
              )}
            >
              {step.label}
            </p>
          </div>
        );
      })}
    </div>
  );
}
